import { LineIcon } from '@/shared/ui/icons/LineIcon';
import { type LineIconProps } from '@/shared/ui/icons/base';

/**
 * Content / metric / tab / role line glyphs (Register A) — KPI badges, achievement & source markers,
 * settings/system tab icons, role-group headers and the chore status flags. Hand-authored single-stroke
 * `currentColor` via {@link LineIcon} so they tint with the card/tone/tab colour. Reference statically
 * (`<HouseIcon/>`) or by name through `LineGlyph` (data-driven KPI/stat surfaces).
 */

/** Circular refresh arrow — the "gone rusty / needs a refresh" freshness nudge (ADR-0046). */
export function RefreshIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M19 12a7 7 0 1 1-2.05-4.95" />
      <path d="M17.6 3.6v3.5h-3.5" />
    </LineIcon>
  );
}

/** A puzzle piece (top + right knobs) — the "finding it tricky" struggling signal (plan-17), kind, never red. */
export function PuzzleIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M5 9a2 2 0 0 1 2-2h2.2a2.3 2.3 0 1 0 5.6 0H17a2 2 0 0 1 2 2v2.2a2.3 2.3 0 1 0 0 5.6V19a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2z" />
    </LineIcon>
  );
}

/** A water drop — the wilting "give it a splash" refresh cue on the kid map (ADR-0046). */
export function DropIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M12 3.5c3.2 4 5.5 7 5.5 10a5.5 5.5 0 1 1-11 0c0-3 2.3-6 5.5-10z" />
    </LineIcon>
  );
}

/** Coin with an inflow arrow — coins earned / income KPI. */
export function CoinInIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5v9M8.5 13l3.5 3.5L15.5 13" />
    </LineIcon>
  );
}

/** Shopping bag — coins spent KPI. */
export function CoinOutIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M6 8h12l-1 11.5H7z" />
      <path d="M9 8V6a3 3 0 0 1 6 0v2" />
    </LineIcon>
  );
}

/** Hourglass — pending / waiting-on-you. */
export function HourglassIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M7 3h10v3l-4 6 4 6v3H7v-3l4-6-4-6z" />
    </LineIcon>
  );
}

/** Compass — quests. */
export function CompassIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="m15.5 8.5-2 5-5 2 2-5z" />
    </LineIcon>
  );
}

/** Pennant flag. */
export function FlagIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M5 21V4M5 4h11l-2 3 2 3H5" />
    </LineIcon>
  );
}

/** Wallet — economy / money settings. */
export function WalletIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h18M16 13.5h2" />
    </LineIcon>
  );
}

/** House — households. */
export function HouseIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M3 10.5 12 3l9 7.5V21a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" />
    </LineIcon>
  );
}

/** Two people — members. */
export function PeopleIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <circle cx="9" cy="8" r="3.3" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16 5a3.3 3.3 0 0 1 0 6.6M21 20a6 6 0 0 0-3.8-5.6" />
    </LineIcon>
  );
}

/** Smiley face — children count. */
export function ChildIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 14s1.3 1.8 3.5 1.8 3.5-1.8 3.5-1.8" />
      <path d="M9 9.5h.01M15 9.5h.01" />
    </LineIcon>
  );
}

/** Calendar — recent / time window. */
export function CalendarIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <rect x="3" y="4.5" width="18" height="16.5" rx="2" />
      <path d="M16 2.5v4M8 2.5v4M3 10h18" />
    </LineIcon>
  );
}

/** Circle with a check — active / OK. */
export function CheckCircleIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </LineIcon>
  );
}

/** Circle with a pause — suspended / paused. */
export function PauseCircleIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M10 9v6M14 9v6" />
    </LineIcon>
  );
}

/** Shield with a check — requires approval. */
export function ApprovalIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      <path d="m9 12 2 2 4-4" />
    </LineIcon>
  );
}

/** Camera — requires photo proof. */
export function CameraIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M3 9a2 2 0 0 1 2-2h2l1.5-2h7L19 7a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <circle cx="12" cy="13" r="3.3" />
    </LineIcon>
  );
}

/** A single person — "needs a parent" / guardian. */
export function ParentIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <circle cx="12" cy="7" r="4" />
      <path d="M4.5 21a7.5 7.5 0 0 1 15 0" />
    </LineIcon>
  );
}

/** Plain shield — privacy / safety. */
export function ShieldIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
    </LineIcon>
  );
}

/** Book — learning. */
export function BookIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M5 4.5A2.5 2.5 0 0 1 7.5 2H20v16H7.5A2.5 2.5 0 0 0 5 20.5z" />
      <path d="M5 4.5v16" />
    </LineIcon>
  );
}

/** Sprout — habits. */
export function SproutIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M12 22V11" />
      <path d="M12 11c0-3 2.2-5.2 5-5.2 0 3-2.2 5.2-5 5.2Z" />
      <path d="M12 13c0-2.6-1.9-4.6-4.5-4.6 0 2.6 1.9 4.6 4.5 4.6Z" />
    </LineIcon>
  );
}

/** Broom — chores. */
export function BroomIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M16 3 9 13M20 8 9 13" />
      <path d="M9 13c-2 1-4 5-5 8h6c1-3 3-6 4-7z" />
    </LineIcon>
  );
}

/** Key — admin-tier / privileged access. */
export function KeyIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <circle cx="8" cy="8" r="4.5" />
      <path d="m11.2 11.2 8 8M16 16l2.2-2.2M18.5 18.5l2-2" />
    </LineIcon>
  );
}

/** Heartbeat line — system status. */
export function PulseIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M3 12h4l2.5-7 4 14 2.5-7H21" />
    </LineIcon>
  );
}

/** Clipboard with lines — audit log. */
export function ClipboardIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4V3h6v1M9 11h6M9 15h4" />
    </LineIcon>
  );
}

/** Bell — events / notifications. */
export function BellIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </LineIcon>
  );
}

/** Terminal — logs. */
export function TerminalIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="m7 9 3 3-3 3M13 15h4" />
    </LineIcon>
  );
}

/** Gift box with a bow — rewards. */
export function GiftIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <rect x="3.5" y="9" width="17" height="11.5" rx="1" />
      <path d="M3 9h18M12 9v11.5" />
      <path d="M12 9c-1-2.6-2.6-4-4.2-3s.6 3 4.2 3Zm0 0c1-2.6 2.6-4 4.2-3s-.6 3-4.2 3Z" />
    </LineIcon>
  );
}
